import { createContext, useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router';

import { BrandContext } from 'context/BrandContext';
import localStorage from 'helpers/localStorage';
import * as Routes from 'helpers/routes';
import { generatePromoAppliedText } from 'helpers/voucherTextGenerator';
import { FUEL_VOUCHER_CODES, SHOW_PROMO_BANNER_LOCAL_STORAGE } from 'lib/constants';
import * as Amplitude from 'services/amplitude';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const UserContext = createContext();
const ProgressBarContext = createContext();
const BasketContext = createContext();
const VoucherContext = createContext();
const PickYourMealsContext = createContext();

const UserContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await RequestService.getFunnelUser();
        if (response.ok && response.user) {
          const user = response.user;
          Amplitude.setAmplitudeUserProperties(user);
          if (user.id) {
            setUser(user);
            ErrorLogging.setUser({ id: user.id, email: user.email, name: user.name });
          }
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (!Amplitude.isInitialized()) Amplitude.init();
  }, []);

  useEffect(() => {
    if (user) {
      try {
        Amplitude.setAmplitudeUserProperties(user);
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    }
  }, [user]);

  return <UserContext.Provider value={{ user, setUser }}>{children}</UserContext.Provider>;
};

const BasketContextProvider = ({ children }) => {
  const [basket, setBasket] = useState(null);
  return <BasketContext.Provider value={{ basket, setBasket }}>{children}</BasketContext.Provider>;
};

const ProgressBarContextProvider = ({ children }) => {
  const [progress, setProgress] = useState({ percent: '15' });

  return (
    <ProgressBarContext.Provider value={{ progress, setProgress }}>
      {children}
    </ProgressBarContext.Provider>
  );
};

const VoucherContextProvider = ({ children }) => {
  const [voucher, setVoucher] = useState(null);
  const [usaaMemberId, setUsaaMemberId] = useState();
  const [promoAppliedText, setPromoAppliedText] = useState('');
  const { brand } = useContext(BrandContext);

  const fuelVoucherCodes = Object.values(FUEL_VOUCHER_CODES);

  const getVoucher = async () => {
    try {
      const response = await RequestService.getVoucher();
      if (response.ok && response.signupVoucher) {
        setVoucher(response.signupVoucher);
      } else {
        setVoucher(null);
      }
    } catch (error) {
      ErrorLogging.captureException(error.message);
    }
  };

  useEffect(() => {
    getVoucher();
  }, []);

  useEffect(() => {
    if (!voucher) return;
    setPromoAppliedText(generatePromoAppliedText(voucher, brand));
  }, [voucher, brand]);

  return (
    <VoucherContext.Provider
      value={{
        voucher,
        setVoucher,
        getVoucher,
        promoAppliedText,
        setPromoAppliedText,
        fuelVoucherCodes: [fuelVoucherCodes],
        usaaMemberId,
        setUsaaMemberId,
      }}
    >
      {children}
    </VoucherContext.Provider>
  );
};

const PickYourMealsContextProvider = ({ children }) => {
  const location = useLocation();
  const hasPickYourMealsSlideOutParam = new URLSearchParams(location.search).get('pym') === 'true';
  const [isPickYourMealsSlideOut, setIsPickYourMealsSlideOut] = useState(
    hasPickYourMealsSlideOutParam
  );

  const isPickYourMeals = location.pathname.startsWith(Routes.PICK_MEALS_MENU_PATH);

  useEffect(() => {
    setIsPickYourMealsSlideOut(hasPickYourMealsSlideOutParam);
  }, [hasPickYourMealsSlideOutParam]);

  useEffect(() => {
    if (!isPickYourMeals) return;

    const handleUserExit = () => {
      localStorage.delete(SHOW_PROMO_BANNER_LOCAL_STORAGE);
    };

    window.addEventListener('beforeunload', handleUserExit);

    return () => {
      window.removeEventListener('beforeunload', handleUserExit);
    };
  }, [isPickYourMeals]);

  return (
    <PickYourMealsContext.Provider
      value={{
        isPickYourMeals,
        isPickYourMealsSlideOut,
      }}
    >
      {children}
    </PickYourMealsContext.Provider>
  );
};

UserContextProvider.propTypes = {
  children: PropTypes.node,
};

ProgressBarContextProvider.propTypes = {
  children: PropTypes.node,
};

BasketContextProvider.propTypes = {
  children: PropTypes.node,
};

VoucherContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.node]),
};

PickYourMealsContextProvider.propTypes = {
  children: PropTypes.node,
};

export {
  BasketContext,
  ProgressBarContext,
  UserContext,
  BasketContextProvider,
  ProgressBarContextProvider,
  UserContextProvider,
  VoucherContextProvider,
  VoucherContext,
  PickYourMealsContext,
  PickYourMealsContextProvider,
};
